import React, { useState, useEffect } from 'react';
import { User, Lock, Bell, AlertTriangle, Save, XCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    company: '',
    jobTitle: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailReports: true,
    weeklyDigest: true,
    trafficAlerts: true,
    productUpdates: false
  });
  
  // Load user data when component mounts
  useEffect(() => {
    const loadUserData = async () => {
      if (auth?.user) {
        setProfileForm({
          name: auth.user.name || '',
          email: auth.user.email || '',
          company: auth.user.company || '',
          jobTitle: auth.user.jobTitle || ''
        });
        
        try {
          // Load notification preferences
          const response = await axios.get('/api/users/me/notifications');
          setNotificationPrefs(response.data);
        } catch (err) {
          console.error('Error loading notification preferences:', err);
        }
      }
    };
    
    loadUserData();
  }, [auth?.user]);
  
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationPrefs(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const validateProfileForm = () => {
    const errors = {};
    
    if (!profileForm.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!profileForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setError(Object.keys(errors).length > 0 ? errors : null);
    return Object.keys(errors).length === 0;
  };
  
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setError(Object.keys(errors).length > 0 ? errors : null);
    return Object.keys(errors).length === 0;
  };
  
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update user profile
      const response = await axios.put('/api/users/me', {
        name: profileForm.name,
        email: profileForm.email,
        company: profileForm.company,
        jobTitle: profileForm.jobTitle
      });
      
      // Update local auth state if available
      if (auth?.updateUser) {
        auth.updateUser(response.data);
      }
      
      setSuccessMessage('Profile updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError({
        form: err.response?.data?.message || 'Failed to update profile. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Change password
      await axios.post('/api/users/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setSuccessMessage('Password changed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError({
        form: err.response?.data?.message || 'Failed to change password. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Update notification preferences
      await axios.put('/api/users/me/notifications', notificationPrefs);
      
      setSuccessMessage('Notification preferences updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating notification preferences:', err);
      setError({
        form: err.response?.data?.message || 'Failed to update notification preferences. Please try again later.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAccountDelete = async () => {
    if (window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      try {
        setIsLoading(true);
        
        // Delete account
        await axios.delete('/api/users/me');
        
        // Logout if auth context is available
        if (auth?.logout) {
          auth.logout();
        }
        
        // Redirect to homepage
        window.location.href = '/';
      } catch (err) {
        console.error('Error deleting account:', err);
        setError({
          form: err.response?.data?.message || 'Failed to delete account. Please try again later.'
        });
        setIsLoading(false);
      }
    }
  };
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      {error?.form && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error.form}
          <button 
            className="absolute top-0 right-0 p-3" 
            onClick={() => setError(prev => ({ ...prev, form: null }))}
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {successMessage}
          <button 
            className="absolute top-0 right-0 p-3" 
            onClick={() => setSuccessMessage(null)}
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="divide-y divide-gray-200">
              <button
                className={`w-full px-6 py-3 text-left ${
                  activeTab === 'profile' 
                  ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500' 
                  : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('profile')}
              >
                <div className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  <span>Profile</span>
                </div>
              </button>
              
              <button
                className={`w-full px-6 py-3 text-left ${
                  activeTab === 'password' 
                  ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500' 
                  : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('password')}
              >
                <div className="flex items-center">
                  <Lock className="h-5 w-5 mr-2" />
                  <span>Password</span>
                </div>
              </button>
              
              <button
                className={`w-full px-6 py-3 text-left ${
                  activeTab === 'notifications' 
                  ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500' 
                  : 'text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setActiveTab('notifications')}
              >
                <div className="flex items-center">
                  <Bell className="h-5 w-5 mr-2" />
                  <span>Notifications</span>
                </div>
              </button>
            </div>
            
            <div className="px-6 py-4 bg-gray-50">
              <button
                onClick={handleAccountDelete}
                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
              >
                <AlertTriangle className="h-4 w-4 mr-1" />
                Delete Account
              </button>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow">
            {activeTab === 'profile' && (
              <div>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
                </div>
                
                <div className="p-6">
                  <form onSubmit={handleProfileSubmit}>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          value={profileForm.name}
                          onChange={handleProfileChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                            error?.name ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {error?.name && (
                          <p className="mt-1 text-sm text-red-600">{error.name}</p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={profileForm.email}
                          onChange={handleProfileChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                            error?.email ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {error?.email && (
                          <p className="mt-1 text-sm text-red-600">{error.email}</p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Company (optional)
                        </label>
                        <input
                          id="company"
                          name="company"
                          type="text"
                          value={profileForm.company}
                          onChange={handleProfileChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                          Job Title (optional)
                        </label>
                        <input
                          id="jobTitle"
                          name="jobTitle"
                          type="text"
                          value={profileForm.jobTitle}
                          onChange={handleProfileChange}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner w-4 h-4 mr-2"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {activeTab === 'password' && (
              <div>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Change Password</h2>
                </div>
                
                <div className="p-6">
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          Current Password
                        </label>
                        <input
                          id="currentPassword"
                          name="currentPassword"
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                            error?.currentPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {error?.currentPassword && (
                          <p className="mt-1 text-sm text-red-600">{error.currentPassword}</p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          id="newPassword"
                          name="newPassword"
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                            error?.newPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {error?.newPassword && (
                          <p className="mt-1 text-sm text-red-600">{error.newPassword}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Password must be at least 8 characters long and include a mix of letters, numbers, and symbols.
                        </p>
                      </div>
                      
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                            error?.confirmPassword ? 'border-red-300' : 'border-gray-300'
                          }`}
                        />
                        {error?.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">{error.confirmPassword}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner w-4 h-4 mr-2"></span>
                            Updating...
                          </>
                        ) : (
                          <>
                            <Lock className="h-4 w-4 mr-2" />
                            Update Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {activeTab === 'notifications' && (
              <div>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Notification Preferences</h2>
                </div>
                
                <div className="p-6">
                  <form onSubmit={handleNotificationSubmit}>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="emailReports"
                            name="emailReports"
                            type="checkbox"
                            checked={notificationPrefs.emailReports}
                            onChange={handleNotificationChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="emailReports" className="font-medium text-gray-700">
                            Monthly Email Reports
                          </label>
                          <p className="text-gray-500">
                            Receive a monthly summary of your website analytics via email.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="weeklyDigest"
                            name="weeklyDigest"
                            type="checkbox"
                            checked={notificationPrefs.weeklyDigest}
                            onChange={handleNotificationChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="weeklyDigest" className="font-medium text-gray-700">
                            Weekly Digest
                          </label>
                          <p className="text-gray-500">
                            Get a weekly summary of your site performance and visitor statistics.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="trafficAlerts"
                            name="trafficAlerts"
                            type="checkbox"
                            checked={notificationPrefs.trafficAlerts}
                            onChange={handleNotificationChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="trafficAlerts" className="font-medium text-gray-700">
                            Traffic Spike Alerts
                          </label>
                          <p className="text-gray-500">
                            Receive notifications when there's a significant increase in your website traffic.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="productUpdates"
                            name="productUpdates"
                            type="checkbox"
                            checked={notificationPrefs.productUpdates}
                            onChange={handleNotificationChange}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="productUpdates" className="font-medium text-gray-700">
                            Product Updates
                          </label>
                          <p className="text-gray-500">
                            Stay informed about new features, improvements, and updates to VisitorStatus.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex justify-end">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner w-4 h-4 mr-2"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Preferences
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;